import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import { setCurrentAnalysisId, getCurrentAnalysisId } from '../utils/appStateManagerAPI';
import i18n from 'i18next';
import { showInfoToast } from '../utils/toastUtils';
import useAnalysisStore from '../stores/useAnalysisStore';
import useAtelier1Store from '../stores/useAtelier1Store';
import useAtelier2Store from '../stores/useAtelier2Store';
import useAtelier3Store from '../stores/useAtelier3Store';
import useAtelier4Store from '../stores/useAtelier4Store';
import useAtelier5Store from '../stores/useAtelier5Store';
import useNotificationStore from '../stores/useNotificationStore';

const AnalysisContext = createContext(null);

export const AnalysisProvider = ({ children }) => {
  const analysisStore = useAnalysisStore();
  const currentAnalysis = analysisStore.currentAnalysis;
  const isLoading = analysisStore.isLoading;

  const { i18n: i18nContext } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const socket = useSocket();

  const lastNotificationRef = useRef({});

  const tabTranslations = {
    'context': 'Contexte (Atelier 1)',
    'business-values': 'Valeurs Métier (Atelier 1)',
    'events': 'Événements Redoutés (Atelier 1)',
    'security': 'Référentiel de Sécurité (Atelier 1)',
    'security-controls': 'Socle de Sécurité (Atelier 1)',
    'risk-treatment': 'Traitement des Risques (Atelier 1)',
    'atelier2-activite1': 'Sources de Risques (Atelier 2)',
    'atelier2-activite2': 'Objectifs Visés (Atelier 2)',
    'atelier2-activite3': 'Couples SR/OV (Atelier 2)',
    'atelier3-activite1': 'Parties Prenantes (Atelier 3)',
    'atelier3-activite2': 'Évaluation des PP (Atelier 3)',
    'atelier3-activite3': 'Cartographie (Atelier 3)',
    'atelier3-activite4': 'Chemins d\'Attaque (Atelier 3)',
    'atelier3-activite5': 'Évaluation des Menaces (Atelier 3)',
    'atelier4-threat-intelligence': 'Threat Intelligence (Atelier 4)',
    'atelier4-cti-scenarios': 'Scénarios Opérationnels (Atelier 4)',
    'atelier5-synthese-scenarios': 'Synthèse des Scénarios (Atelier 5)',
    'atelier5-strategie-traitement': 'Stratégie de Traitement (Atelier 5)',
    'atelier5-mesures-securite': 'Mesures de Sécurité (Atelier 5)',
    'atelier5-evaluation-residuels': 'Évaluation des Risques Résiduels (Atelier 5)',
    'atelier5-plan-traitement': 'Plan de Traitement (Atelier 5)',
    'atelier5-cadre-suivi': 'Cadre de Suivi (Atelier 5)',
    'atelier5-implementation-checklist': 'Suivi d\'Implémentation (Atelier 5)',
    'atelier5-fusion-ptr': 'Fusionnement des PTR (Multi-analyses)'
  };

  useEffect(() => {
    console.log('[GLOBAL_ROOM_DEBUG] useEffect fired:', {
      hasSocket: !!socket,
      socketConnected: socket?.connected,
      currentAnalysisId: currentAnalysis?.id,
      userId: user?._id || user?.id,
      userEmail: user?.email
    });

    if (!socket || !currentAnalysis?.id || !user) {
      return;
    }

    const currentAnalysisId = currentAnalysis.id;
    socket.emit('join_global_analysis_room', { analysisId: currentAnalysisId, user });

    const userId = user?._id || user?.id;
    useNotificationStore.getState().fetchNotifications(currentAnalysisId, userId);

    const handleNotification = async (data) => {
      if (!data || !data.user) return;
      const now = Date.now();
      const notifKey = `${data.user.email || data.user.id}-${data.tab}`;
      const lastTime = lastNotificationRef.current[notifKey] || 0;
      if (now - lastTime < 5000) return;
      lastNotificationRef.current[notifKey] = now;

      const userName = data.user.name || data.user.firstName || data.user.username || data.user.email || 'Un utilisateur';
      const friendlyTabName = tabTranslations[data.tab] || data.tab || 'cette analyse';
      const activeTab = localStorage.getItem('ebiosrm_active_tab');
      const isUserOnThisTab = activeTab === data.tab;

      if (isUserOnThisTab) {
        showInfoToast(`${userName} a modifié "${friendlyTabName}" — sauvegardez ou rechargez pour voir les changements`);
        return;
      }

      showInfoToast(`${userName} a mis à jour: ${friendlyTabName}`);

      const analysisId = useAnalysisStore.getState().currentAnalysis?.id;
      if (!analysisId) return;

      const tab = data.tab;
      const a1Tabs = ['context', 'business-values', 'events', 'security', 'security-controls', 'risk-treatment'];

      if (a1Tabs.includes(tab)) {
        await useAtelier1Store.getState().refreshSlice(analysisId, tab);
      } else if (tab.startsWith('atelier2')) {
        useAtelier2Store.getState().loadData(analysisId);
      } else if (tab.startsWith('atelier3')) {
        useAtelier3Store.getState().refreshSlice(analysisId, tab);
      } else if (tab.startsWith('atelier4')) {
        useAtelier4Store.getState().loadData(analysisId);
      } else if (tab.startsWith('atelier5')) {
        useAtelier5Store.getState().loadData(analysisId);
      } else if (tab === 'analysis-settings') {
        useAnalysisStore.getState().loadAnalysis(analysisId);
      }
    };

    socket.on('analysis_notification', handleNotification);

    const handleNewNotification = (notification) => {
      useNotificationStore.getState().addNotification(notification);
    };

    socket.on('new_notification', handleNewNotification);

    return () => {
      socket.emit('leave_global_analysis_room', { analysisId: currentAnalysisId });
      socket.off('analysis_notification', handleNotification);
      socket.off('new_notification', handleNewNotification);
    };
  }, [socket, currentAnalysis?.id, user?._id, user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWorkshopData = async (analysisId) => {
    if (!analysisId) return;

    useAnalysisStore.getState().setIsWorkshopDataLoading(true);
    useAnalysisStore.getState().setWorkshopDataError(null);

    await Promise.allSettled([
      useAtelier1Store.getState().loadData(analysisId),
      useAtelier2Store.getState().loadData(analysisId),
      useAtelier3Store.getState().loadStakeholders(analysisId),
      useAtelier4Store.getState().loadData(analysisId),
      useAtelier5Store.getState().loadData(analysisId),
    ]);

    useAnalysisStore.getState().setIsWorkshopDataLoading(false);
  };

  const loadAnalysis = async (analysisId) => {
    const result = await analysisStore.loadAnalysis(analysisId);
    if (result) {
      setCurrentAnalysisId(analysisId);
      await loadWorkshopData(analysisId);
      await useAtelier4Store.getState().loadDashboardData(analysisId);
      await useAtelier5Store.getState().loadDashboardData(analysisId);
    }
    return result;
  };

  useEffect(() => {
    if (!isAuthenticated) {
      analysisStore.resetCoreState();
      useAtelier1Store.getState().resetState();
      useAtelier2Store.getState().resetState();
      useAtelier3Store.getState().resetState();
      useAtelier4Store.getState().resetState();
      useAtelier5Store.getState().resetState();
      setCurrentAnalysisId(null);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const lastAnalysisId = getCurrentAnalysisId();
    if (lastAnalysisId && isAuthenticated && user && !currentAnalysis && !isLoading) {
      loadAnalysis(lastAnalysisId).catch((err) => {
        console.warn('[AnalysisContext] Failed to restore last analysis, clearing stored ID:', err);
        localStorage.removeItem('currentAnalysisId');
      });
    }
  }, [isAuthenticated, user, currentAnalysis, isLoading]);

  useEffect(() => {
    if (currentAnalysis?.id && i18n.language) {
      useAtelier1Store.getState().reloadFrameworkDefinitions(currentAnalysis.id);
    }
  }, [i18n.language, currentAnalysis?.id]);

  const selectAnalysis = async (analysisId) => {
    if (!analysisId) {
      analysisStore.resetCoreState();
      useAtelier1Store.getState().resetState();
      useAtelier2Store.getState().resetState();
      useAtelier3Store.getState().resetState();
      useAtelier4Store.getState().resetState();
      useAtelier5Store.getState().resetState();
      setCurrentAnalysisId(null);
      useAnalysisStore.getState().setWorkshopDataError(null);
      return;
    }

    if (currentAnalysis && currentAnalysis.id === analysisId && !isLoading && !analysisStore.isWorkshopDataLoading) {
      return;
    }

    if (isLoading || analysisStore.isWorkshopDataLoading) {
      return;
    }

    analysisStore.setIsLoading(true);
    try {
      await loadAnalysis(analysisId);
    } catch (error) {
      console.error("Error selecting analysis:", error);
    } finally {
      analysisStore.setIsLoading(false);
    }
  };

  const contextValue = {
    selectAnalysis,
  };

  return (
    <AnalysisContext.Provider value={contextValue}>
      {children}
    </AnalysisContext.Provider>
  );
};

export const useAnalysis = () => {
  const context = useContext(AnalysisContext);
  if (!context) {
    throw new Error('useAnalysis must be used within an AnalysisProvider');
  }
  return context;
};